import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ModalProps } from '@swyftx/aviary/atoms/Modal/Modal.styles';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem/ReviewItem';
import { MarginOrderDirection, ProtectionOrderVariant } from '@swyftx/margin/build/types';

import {
  EstimatedMarginProfitLoss,
  TotalMarginPositionSize,
  TotalMarginRequired,
} from '@routes/SwyftxMargin/components/General';

import { useMarginTradePanelStoreUtils } from 'src/lib/margin/hooks/trade';

import { MarginTradeReviewModal } from '../../../MarginTradeReviewModal';

type Props = {
  tradeSide: MarginOrderDirection;
  modalPosition?: ModalProps['position'];
};

const MarginTradePanelReviewFooter: React.FC<Props> = ({ tradeSide, modalPosition = 'dynamic' }) => {
  const {
    isValidTrade,
    buttonColor,
    lots,
    audPrice,
    selectedMarket,
    stopLoss,
    takeProfit,
    tradeType,
    isStalePriceData,
    price,
  } = useMarginTradePanelStoreUtils({
    tradeSide,
  });
  const { t } = useTranslation('margin');

  return (
    <FlexLayout direction='column' className='w-full border-t border-color-border-main p-12' spacing={16}>
      <FlexLayout direction='column' spacing={8}>
        {stopLoss.checked && selectedMarket && (
          <ReviewItem
            title={t('position.estimatedLoss.title')}
            name={t('position.estimatedLoss.name')}
            tooltip={t('position.estimatedLoss.tooltip')}
            underline='dashed'
          >
            <EstimatedMarginProfitLoss
              lots={lots}
              price={audPrice.toString()}
              currentPrice={price}
              protectionPrice={Number(stopLoss.price)}
              protectionVariant={ProtectionOrderVariant.StopLoss}
              instrument={selectedMarket}
              side={tradeSide}
              tradeType={tradeType}
              weight='bold'
            />
          </ReviewItem>
        )}
        {takeProfit.checked && selectedMarket && (
          <ReviewItem
            title={t('position.estimatedProfit.title')}
            name={t('position.estimatedProfit.name')}
            tooltip={t('position.estimatedProfit.tooltip')}
            underline='dashed'
          >
            <EstimatedMarginProfitLoss
              lots={lots}
              price={audPrice.toString()}
              currentPrice={price}
              protectionPrice={Number(takeProfit.price)}
              protectionVariant={ProtectionOrderVariant.TakeProfit}
              instrument={selectedMarket}
              side={tradeSide}
              tradeType={tradeType}
              weight='bold'
            />
          </ReviewItem>
        )}
        <TotalMarginPositionSize
          title={t('position.totalPositionSize.title')}
          lots={lots}
          price={audPrice.toString()}
          contractSize={selectedMarket?.contractSize}
        />
        <TotalMarginRequired
          title={t('position.totalMarginRequired.title')}
          lots={lots}
          price={audPrice.toString()}
          contractSize={selectedMarket?.contractSize}
          maxLeverage={selectedMarket?.maxLeverage}
        />
      </FlexLayout>
      <MarginTradeReviewModal tradeSide={tradeSide} modalPosition={modalPosition}>
        <Button
          variant='filled'
          color={buttonColor}
          tooltip={isStalePriceData() ? t('stalePrice.tooltips.placingOrders') : undefined}
          className='w-full'
          size='lg'
          disabled={!isValidTrade || isStalePriceData()}
        >
          {t('tradeReview.reviewOrder')}
        </Button>
      </MarginTradeReviewModal>
    </FlexLayout>
  );
};

export { MarginTradePanelReviewFooter };
